import { useEffect, useMemo, useState, useTransition } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useRideStationActions } from "@ryde/redux";
import {
  DirectionEnum,
  dropdownStationListAtom,
  errors,
  filterRealRouteStationIds,
  Log,
  simpleResultErrorInfo,
  StationApi,
  stationsForWorkSelector,
  useDrawers,
  useHaveCustomPermissions,
  type ICreateStation,
  type IRideDto,
} from "@ryde/shared";

import { RIDE_ADD_STATION_NEW_STATION } from "../parts/ride-stations-panel.constants";
import type { INewStationFormModel } from "./ride-add-station.models";
import {
  getRideAddStationOptions,
  isRideAddableStation,
} from "./ride-add-station.utils";

export const useRideAddStation = ({
  rideDto,
  isRideWithDuplicatedStationsAllowed,
}: {
  rideDto: Partial<IRideDto>;
  isRideWithDuplicatedStationsAllowed?: boolean;
}) => {
  const methods = useForm<INewStationFormModel>({
    defaultValues: { stationAddId: undefined },
  });
  const { t } = useTranslation();
  const { getValues, setValue, watch } = methods;
  const { openEditFormDrawer } = useDrawers();
  const { rideAddStationWithoutPassengersAction, fetchStations } =
    useRideStationActions();
  const { hasPermissionEditStation, hasPermissionCreateStation } =
    useHaveCustomPermissions();
  const [showQuickStationForm, setShowQuickStationForm] = useState(false);

  const [isStationsDataLoading, startTransition] = useTransition();
  const allStationsDropdownDtos = useRecoilValue(dropdownStationListAtom);
  const stationsForWorkCategories = useRecoilValue(stationsForWorkSelector);
  const watchedStationId: number | undefined = watch("stationAddId");
  const selectStationFirst = t("ride.PLEASE_CHOOSE_STATION_FIRST");

  const rideStationIds = useMemo(() => {
    return filterRealRouteStationIds(rideDto.metadata?.stationIds);
  }, [rideDto.metadata?.stationIds]);

  const stationsDropdownListFiltered = useMemo(() => {
    return getRideAddStationOptions({
      stations: allStationsDropdownDtos,
      rideStationIds,
      isRideWithDuplicatedStationsAllowed,
      stationCategories: stationsForWorkCategories,
      selectedStationId: watchedStationId,
    });
  }, [
    allStationsDropdownDtos,
    isRideWithDuplicatedStationsAllowed,
    rideStationIds,
    stationsForWorkCategories,
    watchedStationId,
  ]);

  /**
   * Selects a station just created or edited here. Checked against the refetched list: the picker's own list
   * is from the last render and does not hold a new station yet. A failed refetch throws to the caller.
   */
  const selectStationAfterRefetch = useEvent(async (stationId: number) => {
    if (!stationId) {
      return;
    }

    const stations = await fetchStations(true);
    const isAddable = isRideAddableStation({
      stationId,
      stations,
      rideStationIds,
      isRideWithDuplicatedStationsAllowed,
    });

    setValue("stationAddId", isAddable ? stationId : undefined);
  });

  /** After the station drawer closes: it saved already, so a failed refetch only warns. */
  const selectStationFromDrawer = useEvent(async (stationId: number) => {
    try {
      await selectStationAfterRefetch(stationId);
    } catch (e) {
      Log.warnAndToast(t("errors.errorFetchingStations"), e);
    }
  });

  const handleStationAdd = useEvent(async () => {
    const stationIdValue = getValues("stationAddId");

    if (!stationIdValue || !Number(stationIdValue)) {
      Log.warnAndToast(selectStationFirst);
      return;
    }

    Log.debug(
      `Adding station to ride ${rideDto.id} with station id: ${stationIdValue}`,
    );
    const stationAdded = await rideAddStationWithoutPassengersAction({
      rideDto,
      stationAddId: Number(stationIdValue),
      isRideWithDuplicatedStationsAllowed,
    });

    if (stationAdded.success) {
      Log.debug("Station added successfully");
      setValue("stationAddId", undefined);
    } else {
      Log.warnAndToastDetailed(
        t(errors.ride.stationNotAdded),
        simpleResultErrorInfo(stationAdded, t),
      );
    }
  });

  const openStationCard = useEvent(async () => {
    if (!watchedStationId) {
      return;
    }

    const simpleResult = await openEditFormDrawer({
      entity: "station",
      id: watchedStationId,
    });

    if (!simpleResult.success || !simpleResult.data?.id) {
      Log.warnAndToast(
        t("errors.bulkFailToUpdateEntities", { entity: t("station.single") }),
      );
      return;
    }

    selectStationFromDrawer(simpleResult.data?.id as number);
  });

  const handleQuickStationSave = useEvent(
    async (data: {
      address: string;
      areaId: number;
      coordinate: { lat: number; lng: number };
    }) => {
      const payload: ICreateStation = {
        name: data.address,
        areaId: data.areaId,
        direction: DirectionEnum.Both,
        isDestination: RIDE_ADD_STATION_NEW_STATION.isDestination,
        metadata: {
          locationInfo: {
            latitude: data.coordinate.lat,
            longitude: data.coordinate.lng,
            fullAddress: data.address,
          },
          isActive: true,
          isOfficial: RIDE_ADD_STATION_NEW_STATION.metadata.isOfficial,
        },
      };

      try {
        Log.debug("Quick station data:", data);

        const station = await StationApi.createStation(payload);

        if (!station) {
          Log.error("No created station");
          return;
        }

        // A failed refetch lands in the catch and keeps the form open.
        await selectStationAfterRefetch(station.id);

        setShowQuickStationForm(false);
      } catch (error) {
        Log.warnAndToast(t("errors.errorCreatingStation"), error);
      }
    },
  );

  const openQuickStationForm = useEvent(() => {
    setShowQuickStationForm(true);
  });

  const handleQuickStationClose = useEvent(() => {
    setShowQuickStationForm(false);
  });

  useEffect(() => {
    startTransition(() => {
      fetchStations();
    });
  }, [fetchStations]);

  return {
    methods,
    isStationsDataLoading,
    stationsDropdownListFiltered,
    watchedStationId,
    hasPermissionEditStation,
    hasPermissionCreateStation,
    showQuickStationForm,
    selectStationFromDrawer,
    openStationCard,
    openQuickStationForm,
    handleQuickStationSave,
    handleQuickStationClose,
    handleStationAdd,
  };
};
